import { Injectable, inject, runInInjectionContext, EnvironmentInjector } from '@angular/core';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { Observable, from, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

/**
 * Caches the result of AdminUsers/{uid} checks so the auth guard does not
 * call Firestore on every navigation. Clear cache on logout.
 */
@Injectable({
  providedIn: 'root',
})
export class AdminCheckService {
  private readonly injector = inject(EnvironmentInjector);
  private readonly firestore = inject(Firestore);

  /** UIDs that have been confirmed as admin in this session. */
  private readonly adminUids = new Set<string>();

  /** Call after successful getDoc(AdminUsers/uid) to skip future checks for this session. */
  setAdmin(uid: string): void {
    this.adminUids.add(uid);
  }

  /** Call on logout so the next session does not use stale cache. */
  clearCache(): void {
    this.adminUids.clear();
  }

  isAdmin(uid: string): boolean {
    return this.adminUids.has(uid);
  }

  /**
   * Returns true if the user is an admin (cached or after fetching), false otherwise.
   * Caches the result on success.
   */
  checkIsAdmin(uid: string): Observable<boolean> {
    if (this.adminUids.has(uid)) {
      return of(true);
    }
    return runInInjectionContext(this.injector, () =>
      from(getDoc(doc(this.firestore, 'AdminUsers', uid)))
    ).pipe(
      map((snap) => {
        if (snap.exists()) {
          this.adminUids.add(uid);
          return true;
        }
        return false;
      }),
      catchError(() => of(false))
    );
  }
}
